<script setup>
import VDevider from "@/Shared/VDevider.vue";
import VButtonSubmit from "@/Shared/Buttons/VButtonSubmit";
import VSelectDefaultWithLabel from "@/Shared/Form/VSelectDefaultWithLabel";
import VRadioWithLabel from "../../Form/VRadioWithLabel.vue";
import VInputWithLabel from "../../Form/VInputWithLabel.vue";
import VRadioTextWithBlockLabel from "../../Form/Survey/VRadioTextWithBlockLabel.vue";

import { useForm } from "@inertiajs/vue3";
import intus from "intus";
import { isRequired, isMin, isInteger } from "intus/rules";

import { scrollOnError } from "@/Helpers/validation";

const props = defineProps({
    additional: Object,
    isShowSubmit: {
        Type: Boolean,
        default: true,
    },
});

const { surveyFields, answers } = props.additional;

const form = useForm({
    survey_location: answers?.survey_location,
    district_of_residence: answers?.district_of_residence,
    gender: answers?.gender,
    marital_status: answers?.marital_status,
    age: parseInt(answers?.age),
    ethnicity: answers?.ethnicity,
    current_employment: answers?.current_employment,
    income_level: answers?.income_level,
});

form.setError(props.additional.errors);

const emits = defineEmits("onNext");

const handleClickNext = () => {
    const validation = intus.validate(form.data(), {
        survey_location: [isRequired()],
        district_of_residence: [isRequired()],
        gender: [isRequired()],
        marital_status: [isRequired()],
        age: [isRequired(), isInteger(), isMin(18)],
        ethnicity: [isRequired()],
        current_employment: [isRequired()],
        income_level: [isRequired()],
    });

    form.clearErrors();
    if (validation.passes()) {
        emits("onNext", form.data());
    } else {
        form.setError(validation.errors());
        setTimeout(() => scrollOnError(), 300);
    }
};

const widthLabel = 4;
const widthInput = 8;
</script>

<template>
    <div>
        <div class="row">
            <div class="col-lg-12 mb-3">
                <div class="mb-3">
                    <VSelectDefaultWithLabel
                        elId="survey_location"
                        v-model:value="form.survey_location"
                        :label="surveyFields['survey_location']?.name"
                        :options="
                            surveyFields['survey_location']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :error="form.errors?.survey_location"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VSelectDefaultWithLabel
                        elId="district_of_residence"
                        v-model:value="form.district_of_residence"
                        :label="surveyFields['district_of_residence']?.name"
                        :options="
                            surveyFields['district_of_residence']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :error="form.errors?.district_of_residence"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioWithLabel
                        elId="gender"
                        v-model:value="form.gender"
                        :label="surveyFields['gender']?.name"
                        :options="
                            surveyFields['gender']?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :error="form.errors?.gender"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="marital_status"
                        v-model:value="form.marital_status"
                        :label="surveyFields['marital_status']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['marital_status']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['marital_status']?.config[
                                'other_label'
                            ]
                        "
                        :error="form.errors?.marital_status"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VInputWithLabel
                        elId="age"
                        v-model:value="form.age"
                        :label="surveyFields['age']?.name"
                        type="number"
                        :error="form.errors?.age"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="ethnicity"
                        v-model:value="form.ethnicity"
                        :label="surveyFields['ethnicity']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['ethnicity']?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :otherOptionLabel="
                            surveyFields['ethnicity']?.config['other_label']
                        "
                        :error="form.errors?.ethnicity"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="current_employment"
                        v-model:value="form.current_employment"
                        :label="surveyFields['current_employment']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['current_employment']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['current_employment']?.config[
                                'other_label'
                            ]
                        "
                        :error="form.errors?.current_employment"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioWithLabel
                        elId="income_level"
                        v-model:value="form.income_level"
                        :label="surveyFields['income_level']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['income_level']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :error="form.errors?.income_level"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>
            </div>
        </div>

        <VDevider />
        <div class="text-end" v-if="isShowSubmit">
            <VButtonSubmit type="button" @onCLickSubmit="handleClickNext">
                Next
            </VButtonSubmit>
        </div>
    </div>
</template>
